import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { subAreaColor } from '@domain/entities/Organization';
import { OCCUPYING_STATUSES } from '@domain/vocabulary';
import { DateRange } from '@domain/value-objects/DateRange';
import { getServices } from '@infrastructure/services';
import { useAccess } from '../../hooks/useAccess';
import { useOwnAreaName } from '../../hooks/useAreaName';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import { liveBalanceRefsFor, liveBalancesByPerson, totalAvailable } from '../../balancePeriod';
import { useSession } from '../../providers/SessionProvider';
import { formatDateRange } from '../../format';

/**
 * The three panels an area administrator's Inicio adds.
 *
 * Split out of `useHomeViewModel` because they answer a different question. An
 * employee's home asks "what is happening to me"; this one asks "what is broken
 * in my area", and the two share almost no data.
 *
 * Everything here is bounded by the area. The roster read is the one list that
 * is not a server-side count, and it has to be — «personas sin líder» and
 * «saldo en riesgo» are properties of individuals, not aggregates. It is capped
 * and the cap is reported rather than silently truncating.
 */

/** How much of the roster the todo counters inspect. */
export const ROSTER_LIMIT = 300;

/** How far ahead «Próximas ausencias» looks. */
const UPCOMING_HORIZON_DAYS = 45;
const UPCOMING_LIMIT = 5;
const COVERAGE_HORIZON_DAYS = 7;
/** Below this, an upcoming absence is imminent enough to warrant the warm chip. */
const SOON_DAYS = 7;

export interface TodoRow {
  readonly id: string;
  readonly count: number;
  readonly title: string;
  readonly note: string;
  readonly color: string;
  readonly ctaLabel: string;
  readonly onOpen: () => void;
}

export interface CoverageRow {
  readonly id: string;
  readonly name: string;
  readonly color: string;
  readonly meta: string;
  readonly tagLabel: string;
  readonly tagBackground: string;
  readonly tagForeground: string;
  /** 0–100, already clamped. */
  readonly percent: number;
  readonly barColor: string;
}

export interface UpcomingRow {
  readonly id: string;
  readonly name: string;
  readonly subAreaName: string;
  readonly rangeLabel: string;
  readonly color: string;
  readonly whenLabel: string;
  readonly soon: boolean;
}

export interface AreaHomeSections {
  readonly todo: readonly TodoRow[];
  readonly todoNote: string;
  readonly todoTag: string;
  readonly coverage: readonly CoverageRow[];
  readonly upcoming: readonly UpcomingRow[];
  readonly subAreaCount: number;
  readonly pendingAccounts: number;
  readonly activeHolidays: number;
  readonly activePeople: number;
  /** Set when the roster exceeded the cap, so the view can say the counts are partial. */
  readonly rosterTruncated: boolean;
}

export const useAreaHomeSections = (enabled: boolean): AreaHomeSections => {
  const session = useSession();
  const config = useRuntimeConfig();
  const { holds, featureOn } = useAccess();
  /*
   * Inicio no sigue al selector, y por eso pide **su** área por nombre.
   *
   * La mitad de esta pantalla es personal —su saldo, sus días por vencer— y esa
   * no puede cambiar de dueño porque alguien mire otra área. Con el nombre
   * enfocado y los datos propios, el encabezado decía «Académico» sobre cifras de
   * Tecnología: una etiqueta que miente es peor que no tener etiqueta. Lo global
   * se mira en «Panorama de la empresa».
   */
  const areaName = useOwnAreaName();
  const navigate = useNavigate();

  const today = getServices().clock.today();

  const go = useCallback(
    (path: string) => () => {
      void navigate(path);
    },
    [navigate],
  );

  const subAreas = useQuery({
    queryKey: ['areaHome', 'subAreas', session.areaId],
    enabled,
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(session.areaId);
      return result.ok ? result.value : [];
    },
  });

  const pendingAccounts = useQuery({
    queryKey: ['areaHome', 'pendingAccounts', session.areaId],
    enabled,
    queryFn: async () => {
      const result = await getServices().people.countByStatus(session.areaId, 'pending');
      return result.ok ? result.value : 0;
    },
  });

  const activePeople = useQuery({
    // Same key the main home model uses, so the two share one read rather than
    // issuing the identical count twice on every admin render.
    queryKey: ['home', 'activePeople', session.areaId],
    enabled,
    queryFn: async () => {
      const result = await getServices().people.countByStatus(session.areaId, 'active');
      return result.ok ? result.value : 0;
    },
  });

  const awaitingSecond = useQuery({
    queryKey: ['areaHome', 'awaitingSecond', session.areaId],
    enabled,
    queryFn: async () => {
      const result = await getServices().requests.count({
        areaId: session.areaId,
        statuses: ['Visto bueno'],
      });
      return result.ok ? result.value : 0;
    },
  });

  const holidays = useQuery({
    queryKey: ['areaHome', 'holidays', today.year],
    enabled: enabled && featureOn('feriados'),
    queryFn: async () => {
      const result = await getServices().holidays.listForYear(today.year);
      return result.ok ? result.value : [];
    },
  });

  /** The roster, capped. Feeds the two per-person counters below. */
  const roster = useQuery({
    queryKey: ['areaHome', 'roster', session.areaId],
    enabled,
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: session.areaId, status: 'active' },
        { limit: ROSTER_LIMIT },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const leaderLinks = useQuery({
    queryKey: ['areaHome', 'leaderLinks', session.areaId],
    enabled,
    queryFn: async () => {
      const result = await getServices().leaders.listByArea(session.areaId);
      return result.ok ? result.value : [];
    },
  });

  const rosterIds = useMemo(() => (roster.data ?? []).map((p) => p.id), [roster.data]);

  const balances = useQuery({
    queryKey: [
      'areaHome',
      'balances',
      session.areaId,
      rosterIds.join(','),
      today.year,
      config.rules.expiryMonths,
    ],
    enabled: enabled && rosterIds.length > 0 && holds('ver_saldos'),
    queryFn: async () => {
      const expiryMonths = config.rules.expiryMonths;
      const result = await getServices().balances.getMany(
        liveBalanceRefsFor(roster.data ?? [], today, expiryMonths),
      );
      if (!result.ok) return [];
      /* Una cifra por persona: lo que puede pedir entre todos sus periodos
         vigentes, que es lo que se acumula y lo que «en riesgo» mide. */
      const byPerson = liveBalancesByPerson(roster.data ?? [], result.value, today, expiryMonths);
      return [...byPerson.values()].map(totalAvailable);
    },
  });

  /** Absences overlapping the coverage window, for the per-sub-area bars. */
  const coverageWindow = useMemo(() => {
    const range = DateRange.create(today, today.addDays(COVERAGE_HORIZON_DAYS));
    return range.ok ? range.value : null;
  }, [today]);

  const outSoon = useQuery({
    queryKey: ['areaHome', 'outSoon', session.areaId, coverageWindow?.toString() ?? ''],
    enabled: enabled && coverageWindow !== null,
    queryFn: async () => {
      if (coverageWindow === null) return [];
      const result = await getServices().requests.list(
        {
          areaId: session.areaId,
          statuses: OCCUPYING_STATUSES,
          touching: coverageWindow,
        },
        { limit: 200 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const upcomingWindow = useMemo(() => {
    const range = DateRange.create(today, today.addDays(UPCOMING_HORIZON_DAYS));
    return range.ok ? range.value : null;
  }, [today]);

  const upcomingRaw = useQuery({
    queryKey: ['areaHome', 'upcoming', session.areaId, upcomingWindow?.toString() ?? ''],
    enabled: enabled && upcomingWindow !== null,
    queryFn: async () => {
      if (upcomingWindow === null) return [];
      const result = await getServices().requests.list(
        { areaId: session.areaId, statuses: OCCUPYING_STATUSES, touching: upcomingWindow },
        { limit: 100 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const subAreaList = useMemo(() => subAreas.data ?? [], [subAreas.data]);
  const subAreaById = useMemo(() => new Map(subAreaList.map((s) => [s.id, s])), [subAreaList]);

  const todo = useMemo<TodoRow[]>(() => {
    const people = roster.data ?? [];
    const links = leaderLinks.data ?? [];

    // Same rule as the admin panel: a leader needs no leader above them, and
    // RR.HH. and the superuser file nothing to escalate.
    const ledPersonIds = new Set(links.map((l) => l.personId));
    const leaderIds = new Set(links.map((l) => l.leaderPersonId));
    const withoutLeader = people.filter(
      (p) =>
        p.role !== 'admin' &&
        p.role !== 'super' &&
        !leaderIds.has(p.id) &&
        !ledPersonIds.has(p.id),
    ).length;

    const subAreasWithoutLead = subAreaList.filter((s) => s.leadPersonId === null).length;

    const atRisk = (balances.data ?? []).filter(
      (available) => available > config.rules.riskThresholdDays,
    ).length;

    const rows: readonly TodoRow[] = [
      {
        id: 'cuentas',
        count: pendingAccounts.data ?? 0,
        title: 'Cuentas por validar',
        note: 'Registros nuevos que aún no pueden solicitar días',
        color: 'var(--color-warning)',
        ctaLabel: 'Validar cuentas',
        onOpen: go('/administrador'),
      },
      {
        id: 'sin-lider',
        count: withoutLeader,
        title: 'Personas sin líder',
        /* Ya no es «no tienen a quién escalar»: sin ningún vínculo las firma quien
           administra el área. Sigue siendo deuda del padrón —quien dirige el equipo
           es quien sabe si el trabajo queda cubierto— pero no un callejón. */
        note: 'Las firma quien administra el área, no su equipo',
        color: 'var(--color-danger)',
        ctaLabel: 'Asignar líderes',
        onOpen: go('/administrador'),
      },
      {
        id: 'sub-sin-lider',
        count: subAreasWithoutLead,
        title: 'Sub áreas sin responsable',
        /* Decía «Nadie aprueba ni recibe sus avisos», y era falso: el responsable
           de una sub área no aprueba nada ni recibe ningún correo — las dos cosas
           salen de `personLeaders`, persona por persona. Lo que sí falta cuando no
           hay responsable es alguien a quien preguntarle por la cobertura. */
        note: 'Nadie responde por su cobertura ni por su tope de gente fuera',
        color: 'var(--color-violet)',
        ctaLabel: 'Elegir responsable',
        onOpen: go('/administrador'),
      },
      {
        id: 'segunda-firma',
        count: awaitingSecond.data ?? 0,
        title: 'Esperando 2º visto bueno',
        note: 'Aprobadas por un líder, falta la segunda firma',
        color: 'var(--color-brand)',
        // An administrator who cannot approve can still audit the trail, so the
        // action points where they can actually act.
        ctaLabel: holds('aprobar') ? 'Ver tablero' : 'Ver en bitácora',
        onOpen: holds('aprobar') ? go('/aprobaciones') : go('/bitacora'),
      },
      {
        id: 'saldo-riesgo',
        count: atRisk,
        title: 'Saldo en riesgo',
        note: `Superan ${String(config.rules.riskThresholdDays)} días sin agendar antes del cierre`,
        color: 'var(--color-success-ink)',
        ctaLabel: 'Abrir reportes',
        onOpen: go('/reportes'),
      },
    ];

    // Only what is actually wrong. A row reading zero is not a task.
    return rows.filter((row) => row.count > 0);
  }, [
    roster.data,
    leaderLinks.data,
    subAreaList,
    balances.data,
    pendingAccounts.data,
    awaitingSecond.data,
    config.rules.riskThresholdDays,
    holds,
    go,
  ]);

  const coverage = useMemo<CoverageRow[]>(() => {
    const people = roster.data ?? [];
    const absences = outSoon.data ?? [];

    return subAreaList.map((sub) => {
      const headcount = people.filter((p) => p.subAreaId === sub.id).length;
      const out = new Set(
        absences.filter((a) => a.subAreaId === sub.id).map((a) => a.personId),
      ).size;
      const cap = sub.maxConcurrentOut;
      const atCap = cap > 0 && out >= cap;
      const percent = cap === 0 ? (out > 0 ? 100 : 0) : Math.min(100, Math.round((out / cap) * 100));

      return {
        id: sub.id,
        name: sub.name,
        color: subAreaColor(sub),
        meta: `${String(out)} fuera / tope ${String(cap)} · ${String(headcount)} ${
          headcount === 1 ? 'persona' : 'personas'
        }`,
        tagLabel: atCap ? 'En el tope' : out > 0 ? 'Con gente fuera' : 'Cubierta',
        tagBackground: atCap
          ? 'var(--color-danger-bg)'
          : out > 0
            ? 'var(--color-warning-bg)'
            : 'var(--color-success-bg)',
        tagForeground: atCap
          ? 'var(--color-danger-ink)'
          : out > 0
            ? 'var(--color-warning-ink)'
            : 'var(--color-success-ink)',
        percent,
        barColor: atCap
          ? 'var(--color-danger)'
          : out > 0
            ? 'var(--color-warning)'
            : 'var(--color-success)',
      };
    });
  }, [subAreaList, roster.data, outSoon.data]);

  const upcoming = useMemo<UpcomingRow[]>(() => {
    const rows = (upcomingRaw.data ?? [])
      .filter((r) => !r.range.start.isBefore(today))
      .sort((a, b) => a.range.start.epochDay - b.range.start.epochDay)
      .slice(0, UPCOMING_LIMIT);

    return rows.map((r) => {
      const days = r.range.start.epochDay - today.epochDay;
      return {
        id: r.id,
        name: r.personName,
        subAreaName: r.subAreaName,
        rangeLabel: formatDateRange(r.range),
        color:
          r.subAreaId === null
            ? 'var(--color-brand)'
            : subAreaColor(subAreaById.get(r.subAreaId)),
        whenLabel: days <= 0 ? 'En curso' : days === 1 ? 'Mañana' : `En ${String(days)} d`,
        soon: days <= SOON_DAYS,
      };
    });
  }, [upcomingRaw.data, today, subAreaById]);

  return {
    todo,
    todoNote: 'Lo que bloquea al área si no se resuelve esta semana. Ordenado por urgencia.',
    todoTag: `Área ${areaName}`,
    coverage,
    upcoming,
    subAreaCount: subAreaList.length,
    pendingAccounts: pendingAccounts.data ?? 0,
    activeHolidays: (holidays.data ?? []).filter((h) => h.enabled).length,
    activePeople: activePeople.data ?? 0,
    rosterTruncated: (roster.data ?? []).length >= ROSTER_LIMIT,
  };
};
